import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form } from 'react-bootstrap';
import { qapi } from '../v1_adapter';

/**
 * Modal for creating new media outlets
 * Handles form submission, client-side validation, and error display
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {function} props.toggle - Callback to toggle modal visibility
 * @param {function} props.onCreateSuccess - Callback on successful creation, receives created outlet
 * @param {string} props.initialName - Optional initial value for the outlet name field
 * @returns {React.ReactElement} Modal component
 */
function CreateMediaOutletModal({
 isOpen, toggle, onCreateSuccess, initialName = '',
}) {
  const [formData, setFormData] = useState({
    name: initialName,
    domain: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: initialName,
    }));
  }, [initialName, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Validate name
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be at most 100 characters';
    }

    // Validate domain format if provided
    if (formData.domain) {
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/;
      if (!domainRegex.test(formData.domain)) {
        newErrors.domain = 'Please enter a valid domain (e.g., example.com)';
      }
    }

    return newErrors;
  };

  const resetForm = () => {
    setFormData({ name: '', domain: '' });
    setErrors({});
    setSubmitError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const outletData = {
        name: formData.name.trim(),
        domain: formData.domain.trim() || null,
      };

      const createdOutlet = await qapi.mediaOutlets.create(outletData);
      onCreateSuccess(createdOutlet);
      resetForm();
      toggle();
    } catch (error) {
      setSubmitError(error.message || 'Failed to create outlet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    resetForm();
    toggle();
  };

  return (
    <Modal show={isOpen} onHide={handleModalClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Media Outlet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitError && (
          <div className="alert alert-danger" role="alert">
            {submitError}
          </div>
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Outlet Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="e.g., TechCrunch"
              value={formData.name}
              onChange={handleChange}
              isInvalid={!!errors.name}
              disabled={isSubmitting}
            />
            {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Domain (optional)</Form.Label>
            <Form.Control
              type="text"
              name="domain"
              placeholder="e.g., techcrunch.com"
              value={formData.domain}
              onChange={handleChange}
              isInvalid={!!errors.domain}
              disabled={isSubmitting}
            />
            {errors.domain && <Form.Control.Feedback type="invalid">{errors.domain}</Form.Control.Feedback>}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Outlet'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

CreateMediaOutletModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
  initialName: PropTypes.string,
};

CreateMediaOutletModal.defaultProps = {
  initialName: '',
};

export default CreateMediaOutletModal;
